/**
 * 语音播放控制器（任务 14.2a）。**全局单例**：同一时刻只允许一条语音在播——
 * 点另一条自动停掉上一条（旧版每气泡各持 AudioPlayer 互不干扰，属体验缺陷，
 * 新版刻意收敛；日志有记）。核心是可注入 AudioLike 的纯状态机，可单测；
 * 浏览器单例与订阅 hook 在文件尾。
 *
 * 状态口径：
 * - key='' 为空闲；非空时 status：loading（src 已设/play() 在途）→ playing →
 *   paused（手动暂停或播完——播完把 currentTime 归 0，再点从头播）/ error
 *   （加载或解码失败，气泡显示「播放失败」，再点重新加载同 URL）。
 * - durationMs 以媒体元数据为准（timeupdate 时读），0=未知——UI 回落 envelope
 *   的 dur_ms；positionMs 随 timeupdate 推进（~4Hz，够画进度条）。
 * - 切会话/卸载消息列表时调 stop()：语音不跨会话续播。
 */

import { useEffect, useState } from 'preact/hooks';

export type VoicePlayStatus = 'loading' | 'playing' | 'paused' | 'error';

export interface VoicePlayerState {
  /** 当前条目 key（消息行 key），'' = 空闲 */
  readonly key: string;
  readonly status: VoicePlayStatus;
  readonly positionMs: number;
  /** 媒体元数据时长，0 = 未知（UI 回落 envelope dur_ms） */
  readonly durationMs: number;
}

type AudioEventName = 'timeupdate' | 'ended' | 'error';

/** HTMLAudioElement 的最小子集（测试注入假实现） */
export interface AudioLike {
  src: string;
  currentTime: number;
  readonly duration: number;
  play(): Promise<void>;
  pause(): void;
  addEventListener(type: AudioEventName, listener: () => void): void;
}

const IDLE: VoicePlayerState = { key: '', status: 'paused', positionMs: 0, durationMs: 0 };

export interface VoicePlayer {
  getState(): VoicePlayerState;
  subscribe(listener: () => void): () => void;
  /** 点击气泡：同 key 播放中=暂停、暂停/出错=（重）播，换 key=停旧播新 */
  toggle(key: string, url: string): void;
  /** 停止并回空闲（消息列表卸载/切会话时调） */
  stop(): void;
}

export function createVoicePlayer(createAudio: () => AudioLike): VoicePlayer {
  let audio: AudioLike | null = null;
  let state = IDLE;
  const listeners = new Set<() => void>();

  const setState = (next: VoicePlayerState) => {
    state = next;
    for (const fn of listeners) fn();
  };

  const mediaDurationMs = (a: AudioLike): number => {
    const sec = a.duration;
    return Number.isFinite(sec) && sec > 0 ? Math.round(sec * 1000) : 0;
  };

  const ensureAudio = (): AudioLike => {
    if (audio !== null) return audio;
    const a = createAudio();
    a.addEventListener('timeupdate', () => {
      if (state.key === '') return;
      setState({
        ...state,
        positionMs: Math.round(a.currentTime * 1000),
        durationMs: mediaDurationMs(a),
      });
    });
    a.addEventListener('ended', () => {
      if (state.key === '') return;
      // 播完回起点：下次点同一条从头播（legacy completed→seek(0)+pause 同口径）
      a.currentTime = 0;
      setState({ ...state, status: 'paused', positionMs: 0 });
    });
    a.addEventListener('error', () => {
      if (state.key === '') return;
      setState({ ...state, status: 'error', positionMs: 0 });
    });
    audio = a;
    return a;
  };

  /** play() 拒绝（解码失败/策略拦截）按加载失败处理；晚到的拒绝不覆盖新条目 */
  const playGuarded = (a: AudioLike, key: string) => {
    a.play().then(
      () => {
        if (state.key === key && state.status === 'loading') {
          setState({ ...state, status: 'playing', durationMs: mediaDurationMs(a) });
        }
      },
      () => {
        if (state.key === key) setState({ ...state, status: 'error' });
      },
    );
  };

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    toggle(key, url) {
      if (url === '') return;
      const a = ensureAudio();
      if (state.key === key) {
        if (state.status === 'loading') return; // play() 在途，等定态
        if (state.status === 'playing') {
          a.pause();
          setState({ ...state, status: 'paused' });
          return;
        }
        if (state.status === 'error') {
          // 重新加载同 URL（src 重设触发重新拉取）
          a.src = url;
          setState({ key, status: 'loading', positionMs: 0, durationMs: 0 });
          playGuarded(a, key);
          return;
        }
        setState({ ...state, status: 'loading' });
        playGuarded(a, key);
        return;
      }
      // 换条目：停旧播新
      if (state.key !== '') a.pause();
      a.src = url;
      setState({ key, status: 'loading', positionMs: 0, durationMs: 0 });
      playGuarded(a, key);
    },
    stop() {
      if (state.key === '') return;
      audio?.pause();
      setState(IDLE);
    },
  };
}

// ---------------------------------------------------------------------------
// 浏览器单例 + 订阅 hook（组件层）
// ---------------------------------------------------------------------------

let singleton: VoicePlayer | null = null;

export function voicePlayer(): VoicePlayer {
  singleton ??= createVoicePlayer(() => new Audio());
  return singleton;
}

/** 空闲快照（本条目非活动时返回，引用稳定不触发重渲染） */
const INACTIVE: VoicePlayerState = IDLE;

/** 订阅某条语音的播放态；非活动条目恒返回 IDLE 快照（引用稳定不空转渲染） */
export function useVoiceEntryState(key: string): VoicePlayerState {
  const [state, setState] = useState<VoicePlayerState>(INACTIVE);
  useEffect(() => {
    const sync = () => {
      const s = voicePlayer().getState();
      setState(s.key === key ? s : INACTIVE);
    };
    sync(); // key 变化/订阅间隙的状态追平
    return voicePlayer().subscribe(sync);
  }, [key]);
  return state;
}
